import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { Trash2, Plus, MessageCircle, Send, Users, Copy, DollarSign, Check, X } from "lucide-react";

const TABS = ["Rules", "Chores", "Tokens", "Categories", "Questions", "Cash-outs", "Co-parents"];

export default function Family() {
  const [tab, setTab] = useState("Rules");

  return (
    <div data-testid="family-page">
      <div className="mb-6">
        <h1 className="text-[32px] md:text-[38px] font-bold tracking-tight text-[#2C342A]">
          Settings
        </h1>
        <p className="text-sm text-[#707A6E]">Rules, chores, tokens & everything the kids see.</p>
      </div>

      <div className="flex gap-2 mb-8 border-b border-[#E4E2DC] overflow-x-auto">
        {TABS.map((t) => (
          <button
            key={t}
            data-testid={`family-tab-${t.toLowerCase()}`}
            onClick={() => setTab(t)}
            className={`px-4 py-3 font-medium text-sm whitespace-nowrap border-b-2 transition-colors ${
              tab === t ? "border-[#5C7A4F] text-[#2C342A]" : "border-transparent text-[#707A6E] hover:text-[#2C342A]"
            }`}
          >
            {t}
          </button>
        ))}
      </div>

      {tab === "Rules" && <RulesPanel />}
      {tab === "Chores" && <ChoresPanel />}
      {tab === "Tokens" && <TokensPanel />}
      {tab === "Categories" && <CategoriesPanel />}
      {tab === "Questions" && <QuestionsPanel />}
      {tab === "Cash-outs" && <CashoutsPanel />}
      {tab === "Co-parents" && <CoParentsPanel />}
    </div>
  );
}

/* ---------- CO-PARENTS ---------- */
function CoParentsPanel() {
  const [members, setMembers] = useState([]);
  const [link, setLink] = useState("");
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const load = () => api.get("/family/members").then((r) => setMembers(r.data));
  useEffect(() => { load(); }, []);
  const invite = async () => {
    setErr(""); setBusy(true);
    try {
      const { data } = await api.post("/family/invite", email ? { email } : {});
      setLink(data.url || `${window.location.origin}/invite?token=${data.token}`);
      setSent(!!data.emailed);
    } catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };
  const copy = async () => { try { await navigator.clipboard.writeText(link); } catch (_) { /* ignore */ } };
  return (
    <div className="space-y-6" data-testid="coparents-panel">
      <div className="card-soft p-6">
        <div className="flex items-center gap-2 mb-3">
          <Users size={16} className="text-[#5C7A4F]" />
          <h3 className="font-heading text-lg font-semibold text-[#2C342A]">Family members</h3>
        </div>
        <div className="space-y-1">
          {members.map((m) => (
            <div key={m.id} className="flex items-center justify-between py-2 border-b border-[#E4E2DC] last:border-0" data-testid={`family-member-${m.id}`}>
              <div>
                <span className="font-medium text-[#2C342A]">{m.name || "(no name)"}</span>
                <span className="text-[#707A6E] text-sm ml-2">{m.email}</span>
              </div>
              {m.is_me && <span className="text-xs text-emerald-700 bg-emerald-500/10 px-2 py-0.5 rounded-full">you</span>}
            </div>
          ))}
        </div>
      </div>

      <div className="card-soft p-6">
        <h3 className="font-heading text-lg font-semibold text-[#2C342A] mb-2">Invite a co-parent</h3>
        <p className="text-[#707A6E] text-sm mb-4">They'll see the same kids, rules, tokens and cash-outs — and can approve/deny anything you can.</p>
        {!link ? (
          <div className="space-y-3">
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                   placeholder="Their email (optional — will send an invite email)"
                   data-testid="invite-email-field"
                   className="w-full rounded-xl border border-[#E4E2DC] bg-white px-4 py-3" />
            <button onClick={invite} disabled={busy} data-testid="invite-generate-btn"
                    className="inline-flex items-center gap-2 rounded-xl bg-[#5C7A4F] text-white px-5 py-3 font-medium hover:bg-[#4B6640] disabled:opacity-50">
              <Users size={16} /> {busy ? "Creating…" : (email ? "Send invite email" : "Generate invite link")}
            </button>
          </div>
        ) : (
          <div>
            {sent && (
              <div className="mb-3 text-sm bg-emerald-500/10 text-emerald-800 rounded-xl px-3 py-2" data-testid="invite-sent-msg">
                Invite emailed to <b>{email}</b>.
              </div>
            )}
            <div className="flex gap-2">
              <input readOnly value={link} className="flex-1 rounded-xl border border-[#E4E2DC] bg-[#F0EFEB] px-3 py-2 text-sm font-mono" data-testid="invite-link-display" />
              <button onClick={copy} className="rounded-xl bg-[#5C7A4F] text-white px-3 hover:bg-[#4B6640]" data-testid="invite-copy-btn"><Copy size={14} /></button>
            </div>
          </div>
        )}
        {err && <p className="text-sm text-[#C06E52] mt-3">{err}</p>}
      </div>
    </div>
  );
}

/* ---------- CASH-OUTS ---------- */
function CashoutsPanel() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("pending");
  const load = () => api.get(`/cashout-requests${filter ? `?status=${filter}` : ""}`).then((r) => setItems(r.data));
  useEffect(() => { load(); }, [filter]);  // eslint-disable-line react-hooks/exhaustive-deps
  const approve = async (id) => { await api.post(`/cashout-requests/${id}/approve`); load(); };
  const deny = async (id) => { await api.post(`/cashout-requests/${id}/deny`); load(); };
  return (
    <div data-testid="cashouts-panel">
      <div className="flex gap-2 mb-4">
        {["pending", "approved", "denied", ""].map((s) => (
          <button key={s || "all"} onClick={() => setFilter(s)} data-testid={`cashout-filter-${s || "all"}`}
                  className={`text-xs uppercase tracking-[0.14em] rounded-full px-3 py-1.5 ${filter === s ? "bg-[#5C7A4F] text-white" : "bg-white border border-[#E4E2DC] text-[#707A6E]"}`}>
            {s || "All"}
          </button>
        ))}
      </div>
      <div className="space-y-2">
        {items.length === 0 && <p className="text-[#707A6E] text-sm">No requests.</p>}
        {items.map((c) => (
          <div key={c.id} className="card-soft p-4 flex items-center justify-between" data-testid={`cashout-item-${c.id}`}>
            <div>
              <div className="text-[#2C342A]"><b>{c.kid_name}</b> — <span className="font-mono">${c.amount.toFixed(2)}</span></div>
              {c.note && <div className="text-xs text-[#707A6E] mt-0.5">"{c.note}"</div>}
              <div className="text-[10px] text-[#707A6E] mt-0.5">{new Date(c.created_at).toLocaleString()}</div>
            </div>
            {c.status === "pending" ? (
              <div className="flex gap-2">
                <button onClick={() => approve(c.id)} data-testid={`approve-${c.id}`}
                        className="inline-flex items-center gap-1 rounded-xl bg-[#5C7A4F] text-white px-3 py-2 text-sm hover:bg-[#4B6640]">
                  <Check size={14} /> Approve
                </button>
                <button onClick={() => deny(c.id)} data-testid={`deny-${c.id}`}
                        className="inline-flex items-center gap-1 rounded-xl border border-[#C06E52] text-[#C06E52] px-3 py-2 text-sm hover:bg-[#C06E52]/10">
                  <X size={14} /> Deny
                </button>
              </div>
            ) : (
              <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${c.status === "approved" ? "text-emerald-700 bg-emerald-500/10" : "text-[#C06E52] bg-[#C06E52]/10"}`}>{c.status}</span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------- RULES ---------- */
function RulesPanel() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ title: "", description: "" });
  const [expanded, setExpanded] = useState(null);
  const load = () => api.get("/rules").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  const add = async (e) => {
    e.preventDefault();
    await api.post("/rules", { ...form, order: items.length });
    setForm({ title: "", description: "" }); load();
  };
  const del = async (id) => { await api.delete(`/rules/${id}`); load(); };

  return (
    <div className="space-y-6">
      <form onSubmit={add} className="card-soft p-6 grid gap-3" data-testid="rule-add-form">
        <input required placeholder="Rule title (e.g. No screens before homework)" value={form.title}
               onChange={(e) => setForm({ ...form, title: e.target.value })}
               className="rounded-xl border border-[#E4E2DC] bg-white px-4 py-3" data-testid="rule-title-input" />
        <textarea placeholder="Description (why this rule exists)" rows={3} value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  className="rounded-xl border border-[#E4E2DC] bg-white px-4 py-3" data-testid="rule-desc-input" />
        <button className="rounded-xl bg-[#5C7A4F] text-white px-5 py-2.5 font-medium hover:bg-[#4B6640] justify-self-start" data-testid="rule-add-btn">
          <Plus size={16} className="inline mr-1" /> Add rule
        </button>
      </form>
      <div className="space-y-2">
        {items.map((r) => (
          <div key={r.id} className="card-soft p-4" data-testid={`rule-${r.id}`}>
            <div className="flex items-center gap-3">
              <button onClick={() => setExpanded(expanded === r.id ? null : r.id)} className="flex-1 text-left">
                <div className="font-medium text-[#2C342A]">{r.title}</div>
              </button>
              <button onClick={() => del(r.id)} className="text-[#C06E52] p-2 hover:bg-[#C06E52]/10 rounded-lg" data-testid={`rule-del-${r.id}`}>
                <Trash2 size={16} />
              </button>
            </div>
            {expanded === r.id && r.description && (
              <p className="text-sm text-[#707A6E] mt-3 whitespace-pre-wrap">{r.description}</p>
            )}
          </div>
        ))}
        {items.length === 0 && <p className="text-[#707A6E] text-sm">No rules yet.</p>}
      </div>
    </div>
  );
}

/* ---------- CHORES ---------- */
function ChoresPanel() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ title: "", description: "" });
  const load = () => api.get("/chores").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  const add = async (e) => { e.preventDefault(); await api.post("/chores", form); setForm({ title: "", description: "" }); load(); };
  const del = async (id) => { await api.delete(`/chores/${id}`); load(); };
  return (
    <div className="space-y-6">
      <form onSubmit={add} className="card-soft p-6 grid gap-3" data-testid="chore-add-form">
        <input required placeholder="Chore title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
               className="rounded-xl border border-[#E4E2DC] bg-white px-4 py-3" data-testid="chore-title-input" />
        <textarea placeholder="Description" rows={2} value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  className="rounded-xl border border-[#E4E2DC] bg-white px-4 py-3" data-testid="chore-desc-input" />
        <button className="rounded-xl bg-[#5C7A4F] text-white px-5 py-2.5 font-medium hover:bg-[#4B6640] justify-self-start" data-testid="chore-add-btn">
          <Plus size={16} className="inline mr-1" /> Add chore
        </button>
      </form>
      <div className="grid md:grid-cols-2 gap-4">
        {items.map((c) => (
          <div key={c.id} className="card-soft p-5" data-testid={`chore-${c.id}`}>
            <div className="flex items-start justify-between">
              <div>
                <div className="font-medium text-[#2C342A]">{c.title}</div>
                {c.description && <div className="text-sm text-[#707A6E] mt-2">{c.description}</div>}
              </div>
              <button onClick={() => del(c.id)} className="text-[#C06E52] p-2 hover:bg-[#C06E52]/10 rounded-lg">
                <Trash2 size={16} />
              </button>
            </div>
          </div>
        ))}
        {items.length === 0 && <p className="text-[#707A6E] text-sm md:col-span-2">No chores yet.</p>}
      </div>
    </div>
  );
}

/* ---------- TOKENS ---------- */
function TokensPanel() {
  const [types, setTypes] = useState([]);
  const [kids, setKids] = useState([]);
  const [wallets, setWallets] = useState({});
  const [form, setForm] = useState({ name: "", category: "", dollar_value: 1 });
  const [award, setAward] = useState({ kid_id: "", token_type_id: "", count: 1 });
  const [payoutState, setPayoutState] = useState({ kid_id: "", amount: 0 });
  const [editing, setEditing] = useState(null); // { id, name, category, dollar_value }

  const load = async () => {
    const [t, k] = await Promise.all([api.get("/tokens"), api.get("/kids")]);
    setTypes(t.data); setKids(k.data);
    const w = {};
    for (const kid of k.data) {
      w[kid.id] = (await api.get(`/kids/${kid.id}/wallet`)).data;
    }
    setWallets(w);
  };
  useEffect(() => { load(); }, []);

  const addType = async (e) => { e.preventDefault(); await api.post("/tokens", form); setForm({ name: "", category: "", dollar_value: 1 }); load(); };
  const doAward = async (e) => { e.preventDefault(); await api.post("/tokens/award", award); load(); };
  const doPayout = async (e) => { e.preventDefault(); await api.post("/tokens/payout", payoutState); setPayoutState({ kid_id: "", amount: 0 }); load(); };
  const saveEdit = async () => {
    if (!editing) return;
    await api.patch(`/tokens/${editing.id}`, {
      name: editing.name, category: editing.category, dollar_value: parseFloat(editing.dollar_value) || 0,
    });
    setEditing(null); load();
  };
  const deleteType = async (t) => {
    if (!window.confirm(`Delete token "${t.name}"? Existing awards to kids stay; nothing new can be awarded.`)) return;
    await api.delete(`/tokens/${t.id}`); load();
  };

  return (
    <div className="space-y-8">
      <div className="card-soft p-6">
        <h3 className="font-heading text-xl font-medium text-[#2C342A] mb-4">Token types</h3>
        <form onSubmit={addType} className="grid md:grid-cols-4 gap-3 mb-4" data-testid="token-add-form">
          <input required placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-xl border border-[#E4E2DC] bg-white px-3 py-2" data-testid="token-name-input" />
          <input placeholder="Category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="rounded-xl border border-[#E4E2DC] bg-white px-3 py-2" data-testid="token-cat-input" />
          <input required type="number" step="0.25" min="0" placeholder="$ value" value={form.dollar_value}
                 onChange={(e) => setForm({ ...form, dollar_value: parseFloat(e.target.value) || 0 })}
                 className="rounded-xl border border-[#E4E2DC] bg-white px-3 py-2" data-testid="token-value-input" />
          <button className="rounded-xl bg-[#5C7A4F] text-white font-medium hover:bg-[#4B6640]" data-testid="token-add-btn">Add token</button>
        </form>
        <div className="space-y-1 text-sm">
          {types.map((t) => (
            <div key={t.id} className="border-b border-[#E4E2DC] py-2" data-testid={`token-type-${t.id}`}>
              {editing?.id === t.id ? (
                <div className="grid md:grid-cols-4 gap-2" data-testid={`token-edit-form-${t.id}`}>
                  <input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                         className="rounded-lg border border-[#E4E2DC] bg-white px-2 py-1" data-testid={`token-edit-name-${t.id}`} />
                  <input value={editing.category || ""} onChange={(e) => setEditing({ ...editing, category: e.target.value })}
                         placeholder="Category" className="rounded-lg border border-[#E4E2DC] bg-white px-2 py-1" data-testid={`token-edit-cat-${t.id}`} />
                  <input type="number" step="0.25" min="0" value={editing.dollar_value}
                         onChange={(e) => setEditing({ ...editing, dollar_value: e.target.value })}
                         className="rounded-lg border border-[#E4E2DC] bg-white px-2 py-1" data-testid={`token-edit-value-${t.id}`} />
                  <div className="flex gap-2">
                    <button onClick={saveEdit} className="flex-1 rounded-lg bg-[#5C7A4F] text-white hover:bg-[#4B6640] text-xs font-medium" data-testid={`token-edit-save-${t.id}`}>Save</button>
                    <button onClick={() => setEditing(null)} className="rounded-lg border border-[#E4E2DC] bg-white px-2 text-xs">Cancel</button>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-between">
                  <span className="text-[#2C342A]"><b>{t.name}</b> {t.category && <span className="text-[#707A6E]">· {t.category}</span>}</span>
                  <div className="flex items-center gap-3">
                    <span className="font-mono">${t.dollar_value.toFixed(2)}</span>
                    <button onClick={() => setEditing({ id: t.id, name: t.name, category: t.category || "", dollar_value: t.dollar_value })}
                            className="text-[#5C7A4F] hover:text-[#4B6640] p-1" data-testid={`token-edit-btn-${t.id}`} title="Edit">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z"/></svg>
                    </button>
                    <button onClick={() => deleteType(t)}
                            className="text-[#C06E52] hover:text-[#A35A42] p-1" data-testid={`token-delete-btn-${t.id}`} title="Delete">
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
          {types.length === 0 && <p className="text-[#707A6E] text-sm">No token types yet.</p>}
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <form onSubmit={doAward} className="card-soft p-6 grid gap-3" data-testid="token-award-form">
          <h3 className="font-heading text-lg font-medium text-[#2C342A]">Award tokens</h3>
          <select required value={award.kid_id} onChange={(e) => setAward({ ...award, kid_id: e.target.value })} className="rounded-xl border border-[#E4E2DC] bg-white px-3 py-2" data-testid="award-kid-select">
            <option value="">Select kid…</option>{kids.map((k) => <option key={k.id} value={k.id}>{k.name}</option>)}
          </select>
          <select required value={award.token_type_id} onChange={(e) => setAward({ ...award, token_type_id: e.target.value })} className="rounded-xl border border-[#E4E2DC] bg-white px-3 py-2" data-testid="award-token-select">
            <option value="">Select token…</option>{types.map((t) => <option key={t.id} value={t.id}>{t.name} (${t.dollar_value})</option>)}
          </select>
          <input type="number" min="1" value={award.count} onChange={(e) => setAward({ ...award, count: parseInt(e.target.value) || 1 })} className="rounded-xl border border-[#E4E2DC] bg-white px-3 py-2" data-testid="award-count-input" />
          <button className="rounded-xl bg-[#5C7A4F] text-white font-medium py-2 hover:bg-[#4B6640]" data-testid="award-btn">Award</button>
        </form>

        <form onSubmit={doPayout} className="card-soft p-6 grid gap-3" data-testid="token-payout-form">
          <h3 className="font-heading text-lg font-medium text-[#2C342A]">Pay out balance</h3>
          <select required value={payoutState.kid_id} onChange={(e) => setPayoutState({ ...payoutState, kid_id: e.target.value })} className="rounded-xl border border-[#E4E2DC] bg-white px-3 py-2" data-testid="payout-kid-select">
            <option value="">Select kid…</option>{kids.map((k) => <option key={k.id} value={k.id}>{k.name} — ${wallets[k.id]?.balance ?? 0}</option>)}
          </select>
          <input type="number" step="0.25" min="0" placeholder="Amount ($) — 0 = full" value={payoutState.amount}
                 onChange={(e) => setPayoutState({ ...payoutState, amount: parseFloat(e.target.value) || 0 })}
                 className="rounded-xl border border-[#E4E2DC] bg-white px-3 py-2" data-testid="payout-amount-input" />
          <button className="rounded-xl bg-[#5C7A4F] text-white font-medium py-2 hover:bg-[#4B6640]" data-testid="payout-btn">Pay out</button>
        </form>
      </div>

      <div className="card-soft p-6">
        <h3 className="font-heading text-lg font-medium text-[#2C342A] mb-3">Kid balances</h3>
        {kids.map((k) => (
          <div key={k.id} className="flex items-center justify-between py-2 border-b border-[#E4E2DC] last:border-0" data-testid={`wallet-${k.id}`}>
            <span className="font-medium text-[#2C342A]">{k.name}</span>
            <span className="font-mono text-[#5C7A4F] font-semibold">${(wallets[k.id]?.balance ?? 0).toFixed(2)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------- CATEGORIES (for reductions) ---------- */
function CategoriesPanel() {
  const [items, setItems] = useState([]);
  const [name, setName] = useState("");
  const load = () => api.get("/reduction-categories").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  const add = async (e) => { e.preventDefault(); await api.post("/reduction-categories", { name }); setName(""); load(); };
  const del = async (id) => { await api.delete(`/reduction-categories/${id}`); load(); };
  return (
    <div className="space-y-4 max-w-md">
      <p className="text-[#707A6E] text-sm">Categories shown in the drop-down when you apply a time reduction.</p>
      <form onSubmit={add} className="flex gap-2" data-testid="cat-add-form">
        <input required value={name} onChange={(e) => setName(e.target.value)} placeholder="Category name"
               className="flex-1 rounded-xl border border-[#E4E2DC] bg-white px-3 py-2" data-testid="cat-input" />
        <button className="rounded-xl bg-[#5C7A4F] text-white px-4 hover:bg-[#4B6640]" data-testid="cat-add-btn">Add</button>
      </form>
      <div className="card-soft p-4 space-y-1">
        {items.map((c) => (
          <div key={c.id} className="flex items-center justify-between py-2 border-b border-[#E4E2DC] last:border-0" data-testid={`cat-${c.id}`}>
            <span>{c.name}</span>
            <button onClick={() => del(c.id)} className="text-[#C06E52] p-1.5 hover:bg-[#C06E52]/10 rounded"><Trash2 size={14} /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------- QUESTIONS (kids' unanswered) ---------- */
function QuestionsPanel() {
  const [items, setItems] = useState([]);
  const [drafts, setDrafts] = useState({});
  const load = () => api.get("/rules/questions").then((r) => setItems(r.data));
  useEffect(() => { load(); const t = setInterval(load, 10000); return () => clearInterval(t); }, []);
  const answer = async (q) => {
    const a = drafts[q.id]; if (!a) return;
    await api.post(`/rules/${q.rule_id}/questions/${q.id}/answer`, { answer: a });
    setDrafts((d) => ({ ...d, [q.id]: "" })); load();
  };
  return (
    <div className="space-y-3">
      {items.length === 0 && <p className="text-[#707A6E] text-sm">No questions from kids yet.</p>}
      {items.map((q) => (
        <div key={q.id} className="card-soft p-5" data-testid={`question-${q.id}`}>
          <div className="flex items-center gap-2 text-xs text-[#707A6E] mb-2">
            <MessageCircle size={12} />
            <span>{q.kid_name || "Kid"}</span>
            <span>·</span>
            <span>{new Date(q.asked_at).toLocaleString()}</span>
          </div>
          <p className="text-[#2C342A]">{q.question}</p>
          {q.answer ? (
            <div className="mt-3 bg-[#5C7A4F]/10 rounded-xl px-3 py-2 text-sm">
              <span className="font-semibold text-[#5C7A4F]">You:</span> {q.answer}
            </div>
          ) : (
            <div className="mt-3 flex gap-2">
              <input placeholder="Type your answer…" value={drafts[q.id] || ""}
                     onChange={(e) => setDrafts({ ...drafts, [q.id]: e.target.value })}
                     className="flex-1 rounded-xl border border-[#E4E2DC] bg-white px-3 py-2" data-testid={`answer-input-${q.id}`} />
              <button onClick={() => answer(q)} className="rounded-xl bg-[#5C7A4F] text-white px-4 hover:bg-[#4B6640]" data-testid={`answer-btn-${q.id}`}>
                <Send size={14} />
              </button>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
